/**
 * Auth provider: Clerk (preferred), AWS Cognito via Amplify, or local dev JWT.
 */
import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useAuth as useClerkAuth, useOrganization, useUser, useClerk } from '@clerk/react';
import { Auth } from 'aws-amplify';
import { useQueryClient } from 'react-query';

import { clearOnboardingCache } from '@/component-library/layouts/ProtectedLayout/ProtectedLayout';
import { isClerkAuthEnabled } from '@/utils/auth/authMode';
import { buildUserFromClerkSession } from '@/utils/auth/clerkUserMapper';
import { buildUserFromCognitoSession } from '@/utils/auth/cognitoUserMapper';
import { buildUserFromDevJwt } from '@/utils/auth/devJwtUser';
import { clearDevAuthLoggedOut, getMockBearerToken, markDevAuthLoggedOut } from '@/utils/auth/mockAuthSession';
import { registerSessionTokenGetter } from '@/utils/auth/sessionTokenBridge';

import type { User } from '../userTypes';

export type { User } from '../userTypes';

interface UseAuth {
  isLoading: boolean;
  isAuthenticated: boolean;
  username: string;
  signIn: (username: string, password: string) => Promise<Result>;
  signOut: () => Promise<void>;
  newPassword: (password: string) => Promise<Result>;
  forgotPassword: (username: string) => Promise<void>;
  forgotPasswordSubmit: (user: string, code: string, password: string) => Promise<void>;
  refreshUser: () => Promise<void>;
  user: User | null;
}

interface Result {
  isSignInSuccess: boolean;
  message: string;
}

type Props = {
  children?: React.ReactNode;
};

const authContext = createContext({} as UseAuth);

const useProvideCognitoAuth = (): UseAuth => {
  const queryClient = useQueryClient();
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [username, setUsername] = useState('');
  const [user, setUser] = useState<User | null>(null);
  const pendingNewPasswordUser = useRef<unknown>(null);

  const hydrateUser = async () => {
    const cognitoUser = await buildUserFromCognitoSession();
    if (cognitoUser) {
      setUser(cognitoUser);
      setUsername(cognitoUser.username);
      setIsAuthenticated(true);
      console.info('[ChurnSignal] Auth session: AWS Cognito (Amplify).');
      return;
    }

    const devJwt = getMockBearerToken();
    const devUser = buildUserFromDevJwt(devJwt);
    if (devUser) {
      setUser(devUser);
      setUsername(devUser.username);
      setIsAuthenticated(true);
      console.info('[ChurnSignal] Auth session: local mock JWT (REACT_APP_DEV_JWT or sessionStorage).');
      return;
    }

    setUser(null);
    setUsername('');
    setIsAuthenticated(false);
    console.warn(
      '[ChurnSignal] Not authenticated: sign in with Cognito or set REACT_APP_DEV_JWT in env/.env.local.user and restart the dev server.',
    );
  };

  useEffect(() => {
    (async () => {
      try {
        await hydrateUser();
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  const signIn = async (u: string, password: string) => {
    try {
      const cognitoUser = await Auth.signIn(u, password);
      if (cognitoUser.challengeName === 'NEW_PASSWORD_REQUIRED') {
        pendingNewPasswordUser.current = cognitoUser;
        return { isSignInSuccess: false, message: 'NEW_PASSWORD_REQUIRED' };
      }
      if (cognitoUser.challengeName) {
        return {
          isSignInSuccess: false,
          message: cognitoUser.challengeName,
        };
      }
      pendingNewPasswordUser.current = null;
      clearDevAuthLoggedOut();
      await hydrateUser();
      return { isSignInSuccess: true, message: '' };
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Sign in failed';
      return { isSignInSuccess: false, message: msg };
    }
  };

  const signOut = async () => {
    try {
      await Auth.signOut();
    } catch {
      /* ignore */
    }
    markDevAuthLoggedOut();
    pendingNewPasswordUser.current = null;
    setUser(null);
    setUsername('');
    setIsAuthenticated(false);
    clearOnboardingCache();
    void queryClient.clear();
  };

  const newPassword = async (password: string) => {
    const pending = pendingNewPasswordUser.current as any;
    if (!pending) {
      return { isSignInSuccess: false, message: 'Session expired. Please sign in again.' };
    }
    try {
      await Auth.completeNewPassword(pending, password, {});
      pendingNewPasswordUser.current = null;
      clearDevAuthLoggedOut();
      await hydrateUser();
      return { isSignInSuccess: true, message: '' };
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Could not set password';
      return { isSignInSuccess: false, message: msg };
    }
  };

  const forgotPassword = async (u: string) => {
    await Auth.forgotPassword(u);
  };

  const forgotPasswordSubmit = async (u: string, code: string, newPassword: string) => {
    await Auth.forgotPasswordSubmit(u, code, newPassword);
  };

  const refreshUser = async () => {
    await hydrateUser();
  };

  return {
    isLoading,
    isAuthenticated,
    username,
    signIn,
    signOut,
    newPassword,
    forgotPassword,
    forgotPasswordSubmit,
    refreshUser,
    user,
  };
};

const useProvideClerkAuth = (): UseAuth => {
  const { isLoaded, isSignedIn, getToken } = useClerkAuth();
  const { signOut: clerkSignOut } = useClerk();
  const { user: clerkUser, isLoaded: isUserLoaded } = useUser();
  const { organization, isLoaded: isOrgLoaded } = useOrganization();
  const queryClient = useQueryClient();
  const [user, setUser] = useState<User | null>(null);
  const [username, setUsername] = useState('');
  const previousOrgIdRef = useRef<string | null | undefined>(undefined);

  const hydrateUser = async () => {
    if (!isSignedIn || !clerkUser) {
      setUser(null);
      setUsername('');
      return;
    }
    const token = await getToken();
    if (!token) {
      setUser(null);
      setUsername('');
      return;
    }
    const mapped = buildUserFromClerkSession(token, clerkUser);
    if (mapped) {
      setUser(mapped);
      setUsername(mapped.username);
      console.info('[ChurnSignal] Auth session: Clerk.');
    }
  };

  useEffect(() => {
    registerSessionTokenGetter(async () => {
      if (!isSignedIn) return null;
      return getToken();
    });
    return () => registerSessionTokenGetter(null);
  }, [getToken, isSignedIn]);

  useEffect(() => {
    if (!isLoaded || !isUserLoaded || !isOrgLoaded) return;

    const orgId = organization?.id ?? null;
    if (previousOrgIdRef.current !== undefined && previousOrgIdRef.current !== orgId) {
      sessionStorage.removeItem('workspace');
      void queryClient.invalidateQueries();
    }
    previousOrgIdRef.current = orgId;

    void hydrateUser();
  }, [isLoaded, isUserLoaded, isOrgLoaded, isSignedIn, clerkUser?.id, organization?.id]);

  const isLoading = !isLoaded || !isUserLoaded || !isOrgLoaded || (Boolean(isSignedIn) && user === null);
  const isAuthenticated = isSignedIn === true;

  const signOut = async () => {
    setUser(null);
    setUsername('');
    clearOnboardingCache();
    void queryClient.clear();
    await clerkSignOut({ redirectUrl: '/sign-in' });
  };

  return {
    isLoading,
    isAuthenticated,
    username,
    signIn: async () => ({
      isSignInSuccess: false,
      message: 'Use the Clerk sign-in form on this page.',
    }),
    signOut,
    newPassword: async () => ({
      isSignInSuccess: false,
      message: 'Password changes are managed in Clerk.',
    }),
    forgotPassword: async () => undefined,
    forgotPasswordSubmit: async () => undefined,
    refreshUser: hydrateUser,
    user,
  };
};

const CognitoAuthProvider: React.FC<Props> = ({ children }) => {
  const auth = useProvideCognitoAuth();
  return <authContext.Provider value={auth}>{children}</authContext.Provider>;
};

const ClerkAuthProvider: React.FC<Props> = ({ children }) => {
  const auth = useProvideClerkAuth();
  return <authContext.Provider value={auth}>{children}</authContext.Provider>;
};

export const ProvideAuth: React.FC<Props> = ({ children }) => {
  if (isClerkAuthEnabled()) {
    return <ClerkAuthProvider>{children}</ClerkAuthProvider>;
  }
  return <CognitoAuthProvider>{children}</CognitoAuthProvider>;
};

export const useAuth = () => useContext(authContext);
